import { useEffect, useState, type ReactElement } from "react";
import { Navigate } from "react-router-dom";
import { Home } from "./Home.js";
import { getEnabledModules } from "../modules/index.js";
import { useLauncherPreferencesQuery } from "../settings/useSettings.js";
import { normalizeLauncherModuleId } from "../settings/roster.js";
import { useAuthStore } from "../auth/auth-store.js";

/**
 * The authenticated index route ("/"). Normally renders the Home dashboard, but
 * on the *first* arrival of an app load it honours the user's `defaultLanding`
 * preference (the live Playbook BE launcher preference, PB-5134) and redirects
 * to the launcher or an enabled module.
 *
 * One-shot, module-scoped guard: the TopBar Home button also routes to "/", so
 * without this every Home click would bounce to the configured landing. It is
 * consumed on the first eligible mount and only resets on a full reload (which
 * re-enters the app). Tests reset it via `__resetLandingRedirectForTests`.
 */
let landingRedirectConsumed = false;

/** How long the first paint waits for the launcher preference before Home. */
export const LANDING_WAIT_MS = 1500;

/**
 * Spend the one-shot landing redirect without applying it. AppLayout calls this
 * when the app opens on any page other than "/", so a later in-app link to "/"
 * (a notification, the Home button) shows Home instead of the configured landing.
 */
export function consumeLandingRedirect(): void {
  landingRedirectConsumed = true;
}

/** Test hook: clear the one-shot landing guard between cases. */
export function __resetLandingRedirectForTests(): void {
  landingRedirectConsumed = false;
}

/** Test hook: whether the one-shot landing guard has been spent. */
export function __isLandingRedirectConsumedForTests(): boolean {
  return landingRedirectConsumed;
}

export function IndexRoute(): ReactElement | null {
  const { data: launcherPrefs, isPending } = useLauncherPreferencesQuery();
  const tenantEnabledModules = useAuthStore(
    (state) => state.claims?.enabledModules,
  );

  // Capture per-mount whether this mount may apply the landing. A later remount
  // (Home button → "/") reads the already-consumed flag and renders Home. The
  // initializer is a pure read; the flag is flipped in the effect below.
  const [eligible] = useState(() => !landingRedirectConsumed);
  // Bounded wait (PB-5134, t11): the preference is normally already cached by
  // the AppLayout prefetch, so a slow or hanging read must not blank the app.
  const [waited, setWaited] = useState(false);

  useEffect(() => {
    if (eligible) landingRedirectConsumed = true;
  }, [eligible]);
  useEffect(() => {
    const timer = window.setTimeout(() => setWaited(true), LANDING_WAIT_MS);
    return () => window.clearTimeout(timer);
  }, []);

  if (!eligible) return <Home />;
  // Hold the first paint only while the preference is still loading, and only
  // for LANDING_WAIT_MS, so a launcher/module landing doesn't flash Home before
  // redirecting. A read that settles without data (it errored) or outlasts the
  // wait falls through to Home rather than leaving the index route blank.
  // Once the bounded wait has expired the fallback is the decision: a
  // preference that lands later must not pull the person away from Home.
  if (!launcherPrefs || waited) return isPending && !waited ? null : <Home />;

  const target = resolveLandingPath(
    launcherPrefs.defaultLanding,
    tenantEnabledModules,
  );
  return target ? <Navigate to={target} replace /> : <Home />;
}

/**
 * Map a `defaultLanding` preference to an absolute path, or null to stay on
 * Home. "launcher" → the launcher; an *enabled* module id → its route base (the
 * AppLayout bootstrap then mints the instance id, exactly as a search "app"
 * selection does). "home", unknown ids, and disabled modules → null. The saved
 * id is a Settings roster id (`knowledge`, not the runtime `knowledge_studio`),
 * so it is resolved the way the Launcher resolves its favourites.
 */
function resolveLandingPath(
  landing: string,
  tenantEnabledModules?: readonly string[],
): string | null {
  if (landing === "home") return null;
  if (landing === "launcher") return "/launcher";
  const module = getEnabledModules(tenantEnabledModules).find(
    (candidate) => candidate.id === normalizeLauncherModuleId(landing),
  );
  return module ? module.routeBase : null;
}
